/**
 * Real MapLibre estate map for tenant installation / LoR0-LoR3 shells.
 */
import { useCallback, useEffect, useRef } from 'react';
import maplibregl, { type GeoJSONSource, type Map as MLMap } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { tokens } from '@/design';
import { applyBasemap, type BasemapId } from '@/engines/maplibre/basemaps';
import type { AssetTreeNode } from './estateClient';

export type EstateMapTool = 'select' | 'point' | 'rectangle' | 'square' | 'polygon' | 'area' | 'corridor' | 'path';

type EstateRepresentation = {
  type: 'point' | 'rectangle' | 'square' | 'polygon' | 'area' | 'corridor' | 'path';
  lon: number;
  lat: number;
  labelLon?: number;
  labelLat?: number;
  coordinates?: [number, number][];
  width?: number;
  height?: number;
  rotation?: number;
  strokeColor?: string;
  fillColor?: string;
  fillOpacity?: number;
  strokeWidth?: number;
  zIndex?: number;
  pointShape?: 'circle' | 'square' | 'diamond' | 'triangle';
};

const ASSET_SOURCE = 'estate-assets';
const ASSET_FILL = 'estate-assets-fill';
const ASSET_LINE = 'estate-assets-line';
const ASSET_PATH = 'estate-assets-path';
const ASSET_POINT = 'estate-assets-point';
const DEFAULT_CENTER: [number, number] = [54.37, 24.45];
const LABEL_MARKER_CLASS = 'estate-map-asset-label';
const MAP_ACCENT_COLOR = tokens.color.accent.primary;
const MAP_ACCENT_STROKE = tokens.color.surface[0];

function flatten(nodes: AssetTreeNode[]): AssetTreeNode[] {
  return nodes.flatMap((node) => [node, ...flatten(node.children ?? [])]);
}

function representationBbox(rep: EstateRepresentation): number[] {
  const width = rep.type === 'point' ? 0.01 : Number(rep.width ?? 0.08);
  const height = rep.type === 'point' ? 0.01 : Number(rep.type === 'square' ? rep.width ?? 0.08 : rep.height ?? 0.05);
  return [
    rep.lon - width / 2,
    rep.lat - height / 2,
    rep.lon + width / 2,
    rep.lat + height / 2,
  ];
}

function closeRing(points: [number, number][]): [number, number][] {
  if (!points.length) return points;
  const first = points[0];
  const last = points[points.length - 1];
  if (first[0] === last[0] && first[1] === last[1]) return points;
  return [...points, first];
}

function centerOf(points: [number, number][]): [number, number] {
  if (!points.length) return DEFAULT_CENTER;
  const lon = points.reduce((sum, point) => sum + point[0], 0) / points.length;
  const lat = points.reduce((sum, point) => sum + point[1], 0) / points.length;
  return [Number(lon.toFixed(6)), Number(lat.toFixed(6))];
}

function isPolygonType(type: EstateRepresentation['type']): boolean {
  return type === 'polygon' || type === 'area';
}

function isLineType(type: EstateRepresentation['type']): boolean {
  return type === 'path' || type === 'corridor';
}

function rotatedBoxPolygon(rep: EstateRepresentation): [number, number][] {
  const width = Math.max(Number(rep.width ?? 0.08), 0.006);
  const height = Math.max(Number(rep.type === 'square' ? rep.width ?? 0.08 : rep.height ?? 0.05), 0.006);
  const angle = ((rep.rotation ?? 0) * Math.PI) / 180;
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const corners = [
    [-width / 2, -height / 2],
    [width / 2, -height / 2],
    [width / 2, height / 2],
    [-width / 2, height / 2],
  ] as const;
  return closeRing(corners.map(([x, y]) => [
    Number((rep.lon + x * cos - y * sin).toFixed(6)),
    Number((rep.lat + x * sin + y * cos).toFixed(6)),
  ]));
}

function pointShapePolygon(rep: EstateRepresentation): [number, number][] | null {
  const shape = rep.pointShape ?? 'circle';
  if (shape === 'circle') return null;
  const size = Math.max(Number(rep.width ?? 0.01), 0.006);
  const half = size / 2;
  const lon = rep.lon;
  const lat = rep.lat;
  if (shape === 'diamond') {
    return closeRing([
      [lon, lat + half],
      [lon + half, lat],
      [lon, lat - half],
      [lon - half, lat],
    ]);
  }
  if (shape === 'triangle') {
    return closeRing([
      [lon, lat + half],
      [lon + half, lat - half],
      [lon - half, lat - half],
    ]);
  }
  return closeRing([
    [lon - half, lat - half],
    [lon + half, lat - half],
    [lon + half, lat + half],
    [lon - half, lat + half],
  ]);
}

function featureFor(row: AssetTreeNode, selectedId: string | null): GeoJSON.Feature {
  const rep = row.representation as EstateRepresentation | null;
  const bbox = row.bbox?.length === 4 ? row.bbox : rep ? representationBbox(rep) : null;
  const selected = row.id === selectedId;
  const properties = {
    id: row.id,
    label: row.label,
    lor: row.lor,
    nodeType: row.node_type,
    selected,
    geometryType: rep?.type ?? (bbox ? 'rectangle' : 'point'),
    strokeColor: rep?.strokeColor ?? MAP_ACCENT_COLOR,
    fillColor: rep?.fillColor ?? MAP_ACCENT_COLOR,
    fillOpacity: rep?.fillOpacity ?? 0.16,
    strokeWidth: rep?.strokeWidth ?? 2,
    zIndex: rep?.zIndex ?? 4,
    pointShape: rep?.pointShape ?? 'circle',
  };
  if (rep?.type === 'point') {
    const shapePolygon = pointShapePolygon(rep);
    if (shapePolygon) {
      return {
        type: 'Feature',
        properties,
        geometry: {
          type: 'Polygon',
          coordinates: [shapePolygon],
        },
      };
    }
    return {
      type: 'Feature',
      properties,
      geometry: {
        type: 'Point',
        coordinates: [rep.lon, rep.lat],
      },
    };
  }
  if (rep && (rep.type === 'rectangle' || rep.type === 'square' || (rep.type === 'corridor' && !rep.coordinates?.length))) {
    return {
      type: 'Feature',
      properties,
      geometry: {
        type: 'Polygon',
        coordinates: [rotatedBoxPolygon(rep)],
      },
    };
  }
  if (rep?.coordinates?.length) {
    const coordinates = rep.coordinates;
    if (isPolygonType(rep.type)) {
      if (coordinates.length >= 3) {
        return {
          type: 'Feature',
          properties,
          geometry: {
            type: 'Polygon',
            coordinates: [closeRing(coordinates)],
          },
        };
      }
      if (coordinates.length >= 2) {
        return {
          type: 'Feature',
          properties,
          geometry: { type: 'LineString', coordinates },
        };
      }
      return {
        type: 'Feature',
        properties,
        geometry: { type: 'Point', coordinates: coordinates[0] },
      };
    }
    if (isLineType(rep.type)) {
      if (coordinates.length >= 2) {
        return {
          type: 'Feature',
          properties,
          geometry: { type: 'LineString', coordinates },
        };
      }
      return {
        type: 'Feature',
        properties,
        geometry: { type: 'Point', coordinates: coordinates[0] },
      };
    }
  }
  if (rep && isLineType(rep.type) && bbox) {
    return {
      type: 'Feature',
      properties,
      geometry: {
        type: 'LineString',
        coordinates: [
          [bbox[0], rep.lat],
          [bbox[2], rep.lat],
        ],
      },
    };
  }
  if (bbox) {
    return {
      type: 'Feature',
      properties,
      geometry: {
        type: 'Polygon',
        coordinates: [[
          [bbox[0], bbox[1]],
          [bbox[2], bbox[1]],
          [bbox[2], bbox[3]],
          [bbox[0], bbox[3]],
          [bbox[0], bbox[1]],
        ]],
      },
    };
  }
  return {
    type: 'Feature',
    properties,
    geometry: {
      type: 'Point',
      coordinates: [DEFAULT_CENTER[0] + row.lor * 0.03, DEFAULT_CENTER[1] + row.lor * 0.02],
    },
  };
}

function syncAssetLayers(map: MLMap, nodes: AssetTreeNode[], selectedId: string | null) {
  const rows = flatten(nodes);
  const collection: GeoJSON.FeatureCollection = {
    type: 'FeatureCollection',
    features: rows.map((row) => featureFor(row, selectedId)),
  };
  const existing = map.getSource(ASSET_SOURCE) as GeoJSONSource | undefined;
  if (existing) {
    existing.setData(collection);
    return;
  }
  map.addSource(ASSET_SOURCE, { type: 'geojson', data: collection });
  map.addLayer({
    id: ASSET_FILL,
    type: 'fill',
    source: ASSET_SOURCE,
    filter: ['==', ['geometry-type'], 'Polygon'],
    paint: {
      'fill-color': ['case', ['==', ['get', 'selected'], true], MAP_ACCENT_COLOR, ['get', 'fillColor']],
      'fill-opacity': ['case', ['==', ['get', 'selected'], true], 0.32, ['get', 'fillOpacity']],
    },
  });
  map.addLayer({
    id: ASSET_LINE,
    type: 'line',
    source: ASSET_SOURCE,
    filter: ['==', ['geometry-type'], 'Polygon'],
    paint: {
      'line-color': ['case', ['==', ['get', 'selected'], true], MAP_ACCENT_COLOR, ['get', 'strokeColor']],
      'line-width': ['case', ['==', ['get', 'selected'], true], 3, ['get', 'strokeWidth']],
    },
  });
  map.addLayer({
    id: ASSET_PATH,
    type: 'line',
    source: ASSET_SOURCE,
    filter: ['==', ['geometry-type'], 'LineString'],
    paint: {
      'line-color': ['case', ['==', ['get', 'selected'], true], MAP_ACCENT_COLOR, ['get', 'strokeColor']],
      'line-width': ['case', ['==', ['get', 'selected'], true], 4, ['get', 'strokeWidth']],
      'line-dasharray': [2, 1],
    },
  });
  map.addLayer({
    id: ASSET_POINT,
    type: 'circle',
    source: ASSET_SOURCE,
    filter: ['all', ['==', ['geometry-type'], 'Point'], ['==', ['get', 'pointShape'], 'circle']],
    paint: {
      'circle-radius': ['case', ['==', ['get', 'selected'], true], 8, 5],
      'circle-color': ['case', ['==', ['get', 'selected'], true], MAP_ACCENT_COLOR, ['get', 'fillColor']],
      'circle-stroke-color': MAP_ACCENT_STROKE,
      'circle-stroke-width': 2,
    },
  });
}

function fitToAssets(map: MLMap, nodes: AssetTreeNode[]) {
  const features = flatten(nodes)
    .map((row) => row.bbox)
    .filter((bbox): bbox is number[] => Array.isArray(bbox) && bbox.length === 4);
  if (!features.length) return;
  const west = Math.min(...features.map((bbox) => bbox[0]));
  const south = Math.min(...features.map((bbox) => bbox[1]));
  const east = Math.max(...features.map((bbox) => bbox[2]));
  const north = Math.max(...features.map((bbox) => bbox[3]));
  map.fitBounds([[west, south], [east, north]], { padding: 60, duration: 400 });
}

function representationCenter(row: AssetTreeNode): [number, number] | null {
  const rep = row.representation as EstateRepresentation | null;
  if (rep && Number.isFinite(rep.lon) && Number.isFinite(rep.lat)) {
    return [Number(rep.labelLon ?? rep.lon), Number(rep.labelLat ?? rep.lat)];
  }
  if (row.bbox?.length === 4) {
    return [
      Number(((row.bbox[0] + row.bbox[2]) / 2).toFixed(6)),
      Number(((row.bbox[1] + row.bbox[3]) / 2).toFixed(6)),
    ];
  }
  return null;
}

function syncAssetLabels(
  map: MLMap,
  nodes: AssetTreeNode[],
  selectedId: string | null,
  markers: Map<string, maplibregl.Marker>,
  onSelect: (id: string) => void,
  getCurrentRepresentation: (assetId: string) => EstateRepresentation | null,
  onRepresentationChange: (assetId: string, representation: EstateRepresentation) => void,
) {
  const rows = flatten(nodes);
  const activeIds = new Set(rows.map((row) => row.id));
  for (const [id, marker] of markers) {
    if (!activeIds.has(id)) {
      marker.remove();
      markers.delete(id);
    }
  }
  for (const row of rows) {
    const center = representationCenter(row);
    if (!center) continue;
    let marker = markers.get(row.id);
    if (!marker) {
      const el = document.createElement('button');
      el.type = 'button';
      el.className = LABEL_MARKER_CLASS;
      el.addEventListener('click', (event) => {
        event.stopPropagation();
        onSelect(row.id);
      });
      marker = new maplibregl.Marker({ element: el, draggable: true, anchor: 'center' })
        .setLngLat(center)
        .addTo(map);
      marker.on('dragend', () => {
        const current = getCurrentRepresentation(row.id);
        if (!current) return;
        const lngLat = marker!.getLngLat();
        onRepresentationChange(row.id, {
          ...current,
          labelLon: Number(lngLat.lng.toFixed(6)),
          labelLat: Number(lngLat.lat.toFixed(6)),
        });
      });
      markers.set(row.id, marker);
    } else {
      marker.setLngLat(center);
    }
    const element = marker.getElement();
    element.textContent = row.label;
    element.classList.toggle('selected', row.id === selectedId);
  }
}

function selectedAssetRepresentation(state: {
  tree: AssetTreeNode[];
  selectedId: string | null;
}): EstateRepresentation | null {
  if (!state.selectedId) return null;
  const row = flatten(state.tree).find((asset) => asset.id === state.selectedId);
  return (row?.representation as EstateRepresentation | null) ?? null;
}

function nextRepresentationForClick(
  state: {
    tree: AssetTreeNode[];
    selectedId: string | null;
    tool: EstateMapTool;
  },
  coordinate: [number, number],
): EstateRepresentation | null {
  if (!state.selectedId || state.tool === 'select') return null;
  const existing = selectedAssetRepresentation(state);
  const baseStyle = {
    strokeColor: existing?.strokeColor ?? MAP_ACCENT_COLOR,
    fillColor: existing?.fillColor ?? MAP_ACCENT_COLOR,
    fillOpacity: existing?.fillOpacity ?? 0.16,
    strokeWidth: existing?.strokeWidth ?? (state.tool === 'path' ? 4 : 2),
    zIndex: existing?.zIndex ?? 4,
    pointShape: existing?.pointShape ?? 'circle',
  };
  const [lon, lat] = coordinate;
  if (state.tool === 'point') {
    return { ...baseStyle, type: 'point', lon, lat, width: 0.01, height: 0.01 };
  }
  if (state.tool === 'rectangle' || state.tool === 'square') {
    const size = state.tool === 'square' ? 0.08 : undefined;
    return {
      ...baseStyle,
      type: state.tool,
      lon,
      lat,
      width: size ?? existing?.width ?? 0.12,
      height: size ?? existing?.height ?? 0.08,
      rotation: existing?.rotation ?? 0,
    };
  }
  const existingPoints =
    existing?.type === state.tool && Array.isArray(existing.coordinates)
      ? existing.coordinates
      : [];
  const coordinates = [...existingPoints, coordinate];
  const [centerLon, centerLat] = centerOf(coordinates);
  return {
    ...baseStyle,
    type: state.tool,
    lon: centerLon,
    lat: centerLat,
    coordinates,
    width: existing?.width ?? (state.tool === 'corridor' ? 0.05 : 0.12),
    height: existing?.height ?? (state.tool === 'corridor' ? 0.22 : 0.08),
    rotation: existing?.rotation ?? (state.tool === 'corridor' ? 35 : 0),
  };
}

export function EstateBasemapPanel({
  tree,
  selectedId,
  tool,
  basemap,
  onSelect,
  onRepresentationChange,
}: {
  tree: AssetTreeNode[];
  selectedId: string | null;
  tool: EstateMapTool;
  basemap: Extract<BasemapId, 'none' | 'osm' | 'carto-positron' | 'carto-dark-matter' | 'esri-imagery'>;
  onSelect: (id: string) => void;
  onRepresentationChange: (assetId: string, representation: EstateRepresentation) => void;
}) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MLMap | null>(null);
  const labelMarkersRef = useRef<Map<string, maplibregl.Marker>>(new Map());
  const initialBasemapRef = useRef(basemap);
  const latestRef = useRef({ tree, selectedId, tool, onSelect, onRepresentationChange });
  latestRef.current = { tree, selectedId, tool, onSelect, onRepresentationChange };

  const getCurrentRepresentation = useCallback((assetId: string): EstateRepresentation | null => {
    const row = flatten(latestRef.current.tree).find((asset) => asset.id === assetId);
    return (row?.representation as EstateRepresentation | null) ?? null;
  }, []);

  useEffect(() => {
    if (!hostRef.current || mapRef.current) return;
    const labelMarkers = labelMarkersRef.current;
    const map = new maplibregl.Map({
      container: hostRef.current,
      style: { version: 8, sources: {}, layers: [] },
      center: DEFAULT_CENTER,
      zoom: 8,
      attributionControl: false,
    });
    map.addControl(new maplibregl.NavigationControl({ visualizePitch: false }), 'top-right');
    map.on('load', () => {
      applyBasemap(map, initialBasemapRef.current);
      syncAssetLayers(map, latestRef.current.tree, latestRef.current.selectedId);
      syncAssetLabels(
        map,
        latestRef.current.tree,
        latestRef.current.selectedId,
        labelMarkers,
        latestRef.current.onSelect,
        getCurrentRepresentation,
        latestRef.current.onRepresentationChange,
      );
      fitToAssets(map, latestRef.current.tree);
    });
    map.on('click', (event) => {
      const state = latestRef.current;
      const rendered = map.queryRenderedFeatures(event.point, {
        layers: [ASSET_FILL, ASSET_LINE, ASSET_PATH, ASSET_POINT].filter((id) => map.getLayer(id)),
      });
      const picked = rendered[0]?.properties?.id;
      if (typeof picked === 'string' && state.tool === 'select') {
        state.onSelect(picked);
        return;
      }
      if (!state.selectedId || state.tool === 'select') return;
      const coordinate: [number, number] = [
        Number(event.lngLat.lng.toFixed(6)),
        Number(event.lngLat.lat.toFixed(6)),
      ];
      const next = nextRepresentationForClick(state, coordinate);
      if (next) state.onRepresentationChange(state.selectedId, next);
    });
    mapRef.current = map;
    return () => {
      for (const marker of labelMarkers.values()) marker.remove();
      labelMarkers.clear();
      map.remove();
      mapRef.current = null;
    };
  }, [getCurrentRepresentation]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;
    applyBasemap(map, basemap);
    syncAssetLayers(map, tree, selectedId);
    syncAssetLabels(map, tree, selectedId, labelMarkersRef.current, onSelect, getCurrentRepresentation, onRepresentationChange);
  }, [basemap, selectedId, tree, onSelect, getCurrentRepresentation, onRepresentationChange]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    map.getCanvas().style.cursor = tool === 'select' ? 'grab' : 'crosshair';
    return () => {
      map.getCanvas().style.cursor = '';
    };
  }, [tool]);

  return (
    <div
      ref={hostRef}
      className="estate-maplibre-host"
      aria-label="Estate basemap"
      data-map-tool={tool}
    />
  );
}
